import React from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import MerchantModal from '@nexidigital/merchantmodal'
import PortalTextInput from '@nexidigital/portaltextinput'
import {
  amountValidator,
  formatFiscalAmount,
  formatNumberString,
  getDividedAmount,
  handleOnlyNumberKeyDown
} from 'Utility/numberMethods'
import { getYupSchema, transactionsSchemas } from 'Schemas/yup'
import formattedMessage, { messages } from './languageModule'
import './OperationActionsModal.scss'

const OperationActionsModal = ({ actions, data, isLoading, isOperation }) => {
  const { action, currency, defaultAmount, minAmount, maxAmount } = data
  const intl = useIntl()

  const formik = useFormik({
    initialValues: {
      amount: formatFiscalAmount(defaultAmount, currency, false),
      minAmount: getDividedAmount(Number(minAmount), currency).toString(),
      maxAmount: maxAmount ? getDividedAmount(Number(maxAmount), currency).toString() : ''
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema: getYupSchema(transactionsSchemas, intl, ['amount']),
    onSubmit: (values) => {
      actions.doSaveForm({ name: 'OperationActionsForm', data: values })
      actions.doOperationCaptureRefund({ currency, isOperation })
    }
  })

  const modalConfigs = {
    CAPTURE: {
      title: intl.formatMessage(messages.capture),
      description: intl.formatMessage(messages.captionDisclaimer),
      inputLabel: intl.formatMessage(messages.capturedAmount),
      isEditable: true
    },
    REFUND: {
      title: intl.formatMessage(messages.refund),
      description: '',
      inputLabel: intl.formatMessage(messages.refundAmount),
      isEditable: true
    },
    VOID: {
      title: intl.formatMessage(messages.void),
      description: '',
      inputLabel: intl.formatMessage(messages.voidedAmount)
    },
    CANCEL: {
      title: intl.formatMessage(messages.cancel),
      description: '',
      inputLabel: intl.formatMessage(messages.cancelAmount)
    },
    CANCEL_REFUND: {
      title: intl.formatMessage(messages.cancel_refund),
      description: '',
      inputLabel: intl.formatMessage(messages.cancel_refund)
    },
    INCREMENTAL: {
      title: intl.formatMessage(messages.incremental),
      description: '',
      inputLabel: intl.formatMessage(messages.incrementalAmount),
      isEditable: true
    }
  }

  const handleChange = (value) => {
    if (!/^[0-9.,]*$/.test(value)) return
    if (amountValidator(value)) {
      formik.setFieldValue('amount', value)
    }
  }

  const handleBlur = (value) => {
    formik.setFieldValue('amount', formatNumberString(value || ''))
    formik.setFieldTouched('amount', true)
  }

  const handleSubmit = () => {
    formik.handleSubmit()
  }

  return (
    <>
      <MerchantModal
        onClose={() => actions.closePopup()}
        closeLabel={intl.formatMessage(messages.closeAriaLabel)}
        title={modalConfigs[action].title}
        description={modalConfigs[action].description}
        primaryButtonLabel={formattedMessage.confirm}
        secondaryButtonLabel={formattedMessage.close}
        primaryAction={handleSubmit}
        secondaryAction={() => actions.closePopup()}
        primaryButtonDisabled={!formik.values.amount || formik.errors.amount}
        secondaryButtonDisabled={isLoading}
        primaryButtonLoading={isLoading}>
        <div className='operation-actions-modal-form'>
          <PortalTextInput
            ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)} 
            id='textInputAmount'
            name='amount'
            label={modalConfigs[action].inputLabel}
            type='text'
            value={formik.values.amount}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={(e) => handleBlur(e.target.value)}
            disabled={!modalConfigs[action].isEditable}
            error={formik.touched.amount && !!formik.errors.amount}
            errorText={formik.errors.amount}
            onKeyDown={handleOnlyNumberKeyDown}
          />
        </div>
      </MerchantModal>
    </>
  )
}

OperationActionsModal.propTypes = {
  actions: PropTypes.object,
  data: PropTypes.shape({
    action: PropTypes.string,
    currency: PropTypes.string,
    defaultAmount: PropTypes.string,
    minAmount: PropTypes.string,
    maxAmount: PropTypes.string
  }).isRequired,
  isLoading: PropTypes.bool,
  isOperation: PropTypes.bool
}

OperationActionsModal.defaultProps = {
  actions: {},
  isLoading: false,
  isOperation: false
}

export default OperationActionsModal
